// Enhanced measurement tools with pixel spacing support and DICOM metadata integration
import { metaData } from '@cornerstonejs/core';
import * as cornerstone from 'cornerstone-core';

// Measurement tool types
export const MEASUREMENT_TOOL_TYPES = {
  LENGTH: 'length',
  AREA: 'area',
  ELLIPSE: 'ellipse',
  ANGLE: 'angle',
  VELOCITY: 'velocity',
  VTI: 'vti',
  M_MODE: 'mmode',
  DOPPLER: 'doppler'
};

// Pixel spacing extraction from DICOM metadata
export class PixelSpacingManager {
  static extractPixelSpacing(imageId) {
    try {
      // Check if this is an AHI image or video
      if (typeof imageId === 'string' && imageId.startsWith('ahi:')) {
        // Get pixel spacing from AHI cache
        const cachedData = window.ahiImageCache && window.ahiImageCache[imageId];
        if (cachedData && cachedData.metadata && cachedData.metadata.pixelSpacing) {
          const spacing = cachedData.metadata.pixelSpacing;
          if (Array.isArray(spacing) && spacing.length >= 2) {
            return {
              rowSpacing: parseFloat(spacing[0]), // mm per pixel (row)
              columnSpacing: parseFloat(spacing[1]), // mm per pixel (column)
              unit: 'mm',
              source: 'AHI_PixelSpacing'
            };
          }
        }
        
        // Try to get from AHI DICOM metadata
        if (cachedData && cachedData.metadata) {
          const dicomMeta = cachedData.metadata;
          
          // Check for SequenceOfUltrasoundRegions (for Doppler/US images)
          if (dicomMeta.SequenceOfUltrasoundRegions && Array.isArray(dicomMeta.SequenceOfUltrasoundRegions)) {
            console.log('Found SequenceOfUltrasoundRegions:', dicomMeta.SequenceOfUltrasoundRegions);
            
            const regions = dicomMeta.SequenceOfUltrasoundRegions;
            let targetRegion = null;
            
            // Look for a region with PhysicalDeltaX and PhysicalDeltaY
            // Prefer regions with spatial data (RegionDataType 1 or 2) over Doppler (RegionDataType 3 or 4)
            for (let i = 0; i < regions.length; i++) {
              const region = regions[i];
              console.log(`Region ${i}:`, region);
              
              if (region.PhysicalDeltaX !== undefined && region.PhysicalDeltaY !== undefined) {
                // For spatial measurements, prefer regions with PhysicalUnitsXDirection = 3 (cm)
                if (region.PhysicalUnitsXDirection === 3 && region.PhysicalUnitsYDirection === 3) {
                  targetRegion = region;
                  console.log(`Selected spatial region ${i} for pixel spacing`);
                  break;
                } else if (!targetRegion) {
                  // Fallback to any region with physical delta values
                  targetRegion = region;
                  console.log(`Fallback to region ${i} for pixel spacing`);
                }
              }
            }
            
            if (targetRegion) {
              console.log('Using region for pixel spacing:', targetRegion);
              
              // Use PhysicalDeltaX and PhysicalDeltaY directly from the DICOM metadata
              // These represent the physical distance per pixel in the units specified by PhysicalUnits
              let xSpacing = targetRegion.PhysicalDeltaX;
              let ySpacing = targetRegion.PhysicalDeltaY;
              let unit = 'mm'; // Default unit for final measurements
              
              // Handle the actual units based on PhysicalUnitsXDirection and PhysicalUnitsYDirection
              // Reference: DICOM Standard PS3.3-2023e Table C.8.5.5-1
              // 0001H = Percent, 0002H = dB, 0003H = cm, 0004H = seconds, 0007H = cm/sec
              
              if (targetRegion.PhysicalUnitsXDirection === 3) { // 0003H = cm
                // PhysicalDelta values are in cm per pixel
                // For consistency with measurement display, convert to mm per pixel
                xSpacing = xSpacing * 10; // convert cm to mm
                unit = 'mm';
                console.log(`PhysicalUnitsXDirection = 3 (cm): converting ${targetRegion.PhysicalDeltaX} cm/pixel to ${xSpacing} mm/pixel`);
              } else if (targetRegion.PhysicalUnitsXDirection === 1) { // 0001H = Percent
                // Keep as-is for percentage measurements
                unit = '%';
                console.log(`PhysicalUnitsXDirection = 1 (Percent): using ${xSpacing} %/pixel`);
              } else if (targetRegion.PhysicalUnitsXDirection === 2) { // 0002H = dB
                // Keep as-is for decibel measurements
                unit = 'dB';
                console.log(`PhysicalUnitsXDirection = 2 (dB): using ${xSpacing} dB/pixel`);
              } else if (targetRegion.PhysicalUnitsXDirection === 4) { // 0004H = seconds
                // Keep as-is for time-based measurements
                unit = 'seconds';
                console.log(`PhysicalUnitsXDirection = 4 (seconds): using ${xSpacing} seconds/pixel`);
              } else if (targetRegion.PhysicalUnitsXDirection === 7) { // 0007H = cm/sec
                // Keep as-is for velocity measurements
                unit = 'cm/sec';
                console.log(`PhysicalUnitsXDirection = 7 (cm/sec): using ${xSpacing} cm/sec/pixel`);
              } else {
                // Unknown units - log warning and assume mm per pixel
                console.warn('Unknown PhysicalUnitsXDirection:', targetRegion.PhysicalUnitsXDirection, 'assuming mm per pixel');
                unit = 'mm';
              }
              
              if (targetRegion.PhysicalUnitsYDirection === 3) { // 0003H = cm
                // PhysicalDelta values are in cm per pixel, convert to mm per pixel
                ySpacing = ySpacing * 10; // convert cm to mm
                console.log(`PhysicalUnitsYDirection = 3 (cm): converting ${targetRegion.PhysicalDeltaY} cm/pixel to ${ySpacing} mm/pixel`);
              } else if (targetRegion.PhysicalUnitsYDirection === 1) { // 0001H = Percent
                // Keep as-is for percentage measurements
                console.log(`PhysicalUnitsYDirection = 1 (Percent): using ${ySpacing} %/pixel`);
              } else if (targetRegion.PhysicalUnitsYDirection === 2) { // 0002H = dB
                // Keep as-is for decibel measurements
                console.log(`PhysicalUnitsYDirection = 2 (dB): using ${ySpacing} dB/pixel`);
              } else if (targetRegion.PhysicalUnitsYDirection === 4) { // 0004H = seconds
                // Keep as-is for time-based measurements
                console.log(`PhysicalUnitsYDirection = 4 (seconds): using ${ySpacing} seconds/pixel`);
              } else if (targetRegion.PhysicalUnitsYDirection === 7) { // 0007H = cm/sec
                // Keep as-is for velocity measurements
                console.log(`PhysicalUnitsYDirection = 7 (cm/sec): using ${ySpacing} cm/sec/pixel`);
              } else {
                // Unknown units - log warning
                console.warn('Unknown PhysicalUnitsYDirection:', targetRegion.PhysicalUnitsYDirection);
              }
              
              console.log(`Extracted pixel spacing: X=${xSpacing} Y=${ySpacing} ${unit}`);
              console.log(`Original PhysicalDelta values: X=${targetRegion.PhysicalDeltaX} Y=${targetRegion.PhysicalDeltaY}`);
              console.log(`Physical units: X=${targetRegion.PhysicalUnitsXDirection} Y=${targetRegion.PhysicalUnitsYDirection}`);
              
              return {
                rowSpacing: parseFloat(ySpacing),
                columnSpacing: parseFloat(xSpacing),
                unit: unit,
                source: 'AHI_UltrasoundRegions',
                physicalUnitsX: targetRegion.PhysicalUnitsXDirection,
                physicalUnitsY: targetRegion.PhysicalUnitsYDirection,
                regionIndex: regions.indexOf(targetRegion),
                originalPhysicalDeltaX: targetRegion.PhysicalDeltaX,
                originalPhysicalDeltaY: targetRegion.PhysicalDeltaY
              };
            } else {
              console.log('No suitable ultrasound region found with PhysicalDelta values');
            }
          }
          
          // Check for PixelSpacing in DICOM metadata
          if (dicomMeta.PixelSpacing && Array.isArray(dicomMeta.PixelSpacing) && dicomMeta.PixelSpacing.length >= 2) {
            return {
              rowSpacing: parseFloat(dicomMeta.PixelSpacing[0]),
              columnSpacing: parseFloat(dicomMeta.PixelSpacing[1]),
              unit: 'mm',
              source: 'AHI_DICOM_PixelSpacing'
            };
          }
          
          // Check for ImagerPixelSpacing
          if (dicomMeta.ImagerPixelSpacing && Array.isArray(dicomMeta.ImagerPixelSpacing) && dicomMeta.ImagerPixelSpacing.length >= 2) {
            return {
              rowSpacing: parseFloat(dicomMeta.ImagerPixelSpacing[0]),
              columnSpacing: parseFloat(dicomMeta.ImagerPixelSpacing[1]),
              unit: 'mm',
              source: 'AHI_DICOM_ImagerPixelSpacing'
            };
          }
        }
        
        // If no direct match found, try to find video cache entries that might contain this metadata
        if (window.ahiImageCache) {
          for (const [cacheKey, cacheData] of Object.entries(window.ahiImageCache)) {
            if (cacheKey.startsWith('ahi:video/') && cacheData.metadata) {
              console.log('Checking video cache entry for pixel spacing:', cacheKey);
              const dicomMeta = cacheData.metadata;
              
              // Check for SequenceOfUltrasoundRegions in video metadata
              if (dicomMeta.SequenceOfUltrasoundRegions && Array.isArray(dicomMeta.SequenceOfUltrasoundRegions)) {
                console.log('Found SequenceOfUltrasoundRegions in video cache:', dicomMeta.SequenceOfUltrasoundRegions);
                
                const regions = dicomMeta.SequenceOfUltrasoundRegions;
                let targetRegion = null;
                
                // Look for a region with PhysicalDeltaX and PhysicalDeltaY
                for (let i = 0; i < regions.length; i++) {
                  const region = regions[i];
                  
                  if (region.PhysicalDeltaX !== undefined && region.PhysicalDeltaY !== undefined) {
                    // For spatial measurements, prefer regions with PhysicalUnitsXDirection = 3 (cm)
                    if (region.PhysicalUnitsXDirection === 3 && region.PhysicalUnitsYDirection === 3) {
                      targetRegion = region;
                      console.log(`Selected spatial region ${i} from video cache for pixel spacing`);
                      break;
                    } else if (!targetRegion) {
                      // Fallback to any region with physical delta values
                      targetRegion = region;
                      console.log(`Fallback to region ${i} from video cache for pixel spacing`);
                    }
                  }
                }
                
                if (targetRegion) {
                  console.log('Using video cache region for pixel spacing:', targetRegion);
                  
                  // Use PhysicalDeltaX and PhysicalDeltaY directly - these are already in the correct units
                  let xSpacing = targetRegion.PhysicalDeltaX;
                  let ySpacing = targetRegion.PhysicalDeltaY;
                  let unit = 'mm'; // Default unit for measurements
                  
                  // Determine the actual unit based on PhysicalUnitsXDirection and PhysicalUnitsYDirection
                  if (targetRegion.PhysicalUnitsXDirection === 3) { // cm
                    // PhysicalDelta values are already in cm per pixel, convert to mm per pixel for consistency
                    xSpacing = xSpacing * 10; // convert cm to mm
                    unit = 'mm';
                  } else if (targetRegion.PhysicalUnitsXDirection === 4) { // seconds
                    unit = 'seconds';
                  } else if (targetRegion.PhysicalUnitsXDirection === 7) { // cm/sec
                    unit = 'cm/sec';
                  } else {
                    // Unknown units - assume the values are already in mm per pixel
                    console.log('Unknown PhysicalUnitsXDirection:', targetRegion.PhysicalUnitsXDirection, 'assuming mm per pixel');
                    unit = 'mm';
                  }
                  
                  if (targetRegion.PhysicalUnitsYDirection === 3) { // cm
                    ySpacing = ySpacing * 10; // convert cm to mm
                  } else if (targetRegion.PhysicalUnitsYDirection === 7) { // cm/sec
                    // For velocity measurements - keep Y spacing as-is
                  }
                  
                  console.log(`Extracted pixel spacing from video cache: X=${xSpacing} Y=${ySpacing} ${unit}`);
                  console.log(`Original PhysicalDelta values: X=${targetRegion.PhysicalDeltaX} Y=${targetRegion.PhysicalDeltaY}`);
                  console.log(`Physical units: X=${targetRegion.PhysicalUnitsXDirection} Y=${targetRegion.PhysicalUnitsYDirection}`);
                  
                  return {
                    rowSpacing: parseFloat(ySpacing),
                    columnSpacing: parseFloat(xSpacing),
                    unit: unit,
                    source: 'AHI_Video_UltrasoundRegions',
                    physicalUnitsX: targetRegion.PhysicalUnitsXDirection,
                    physicalUnitsY: targetRegion.PhysicalUnitsYDirection,
                    regionIndex: regions.indexOf(targetRegion),
                    cacheKey: cacheKey,
                    originalPhysicalDeltaX: targetRegion.PhysicalDeltaX,
                    originalPhysicalDeltaY: targetRegion.PhysicalDeltaY
                  };
                }
              }
            }
          }
        }
        
        console.warn('No pixel spacing found in AHI metadata for:', imageId);
        return null;
      }

      // For regular DICOM files, use existing logic
      // Try to get pixel spacing from metadata
      const pixelSpacing = metaData.get('imagePlaneModule', imageId)?.pixelSpacing ||
                          metaData.get('imagePixelModule', imageId)?.pixelSpacing;
      
      if (pixelSpacing && Array.isArray(pixelSpacing) && pixelSpacing.length >= 2) {
        return {
          rowSpacing: parseFloat(pixelSpacing[0]), // mm per pixel (row)
          columnSpacing: parseFloat(pixelSpacing[1]), // mm per pixel (column)
          unit: 'mm',
          source: 'PixelSpacing'
        };
      }

      // Try imager pixel spacing
      const imagerPixelSpacing = metaData.get('generalEquipmentModule', imageId)?.imagerPixelSpacing;
      if (imagerPixelSpacing && Array.isArray(imagerPixelSpacing) && imagerPixelSpacing.length >= 2) {
        return {
          rowSpacing: parseFloat(imagerPixelSpacing[0]),
          columnSpacing: parseFloat(imagerPixelSpacing[1]),
          unit: 'mm',
          source: 'ImagerPixelSpacing'
        };
      }

      // Try to get from cornerstone-core metadata
      if (typeof imageId === 'string') {
        const metadata = cornerstone.metaData.get('imagePlaneModule', imageId);
        if (metadata && metadata.pixelSpacing) {
          return {
            rowSpacing: parseFloat(metadata.pixelSpacing[0]),
            columnSpacing: parseFloat(metadata.pixelSpacing[1]),
            unit: 'mm',
            source: 'CornerstoneMetadata'
          };
        }
      }

      return null;
    } catch (error) {
      console.warn('Error extracting pixel spacing:', error);
      return null;
    }
  }

  static getAveragePixelSpacing(pixelSpacing) {
    if (!pixelSpacing) return null;
    return (pixelSpacing.rowSpacing + pixelSpacing.columnSpacing) / 2;
  }

  static convertPixelsToMM(pixels, pixelSpacing) {
    if (!pixelSpacing) return null;
    const avgSpacing = this.getAveragePixelSpacing(pixelSpacing);
    return pixels * avgSpacing;
  }

  static convertPixelsToArea(pixelsSquared, pixelSpacing) {
    if (!pixelSpacing) return null;
    return pixelsSquared * pixelSpacing.rowSpacing * pixelSpacing.columnSpacing;
  }
}

// Enhanced measurement result processor
export class MeasurementProcessor {
  static processLengthMeasurement(annotation, imageId) {
    if (!annotation || !annotation.data || !annotation.data.handles) {
      return null;
    }

    const handles = annotation.data.handles;
    if (!handles.start || !handles.end) {
      return null;
    }

    // Calculate pixel distance
    const dx = handles.end.x - handles.start.x;
    const dy = handles.end.y - handles.start.y;
    const pixelDistance = Math.sqrt(dx * dx + dy * dy);

    // Get pixel spacing
    const pixelSpacing = PixelSpacingManager.extractPixelSpacing(imageId);
    
    let result = {
      pixelValue: pixelDistance,
      pixelSpacing: pixelSpacing,
      timestamp: new Date().toISOString()
    };

    if (pixelSpacing) {
      const mmValue = PixelSpacingManager.convertPixelsToMM(pixelDistance, pixelSpacing);
      result.value = mmValue / 10; // Convert mm to cm
      result.unit = 'cm';
      result.hasRealWorldUnits = true;
    } else {
      result.value = pixelDistance;
      result.unit = 'pixels';
      result.hasRealWorldUnits = false;
      result.warning = 'No pixel spacing data available - measurements in pixels only';
    }

    return result;
  }

  static processAreaMeasurement(annotation, imageId) {
    if (!annotation || !annotation.data) {
      return null;
    }

    let pixelArea = 0;

    // Handle different area measurement types
    if (annotation.data.cachedStats && annotation.data.cachedStats.area) {
      pixelArea = annotation.data.cachedStats.area;
    } else if (annotation.data.handles && annotation.data.handles.points) {
      // Calculate area from polygon points
      const points = annotation.data.handles.points;
      pixelArea = this.calculatePolygonArea(points);
    }

    if (pixelArea === 0) {
      return null;
    }

    const pixelSpacing = PixelSpacingManager.extractPixelSpacing(imageId);
    
    let result = {
      pixelValue: pixelArea,
      pixelSpacing: pixelSpacing,
      timestamp: new Date().toISOString()
    };

    if (pixelSpacing) {
      const mmSquaredValue = PixelSpacingManager.convertPixelsToArea(pixelArea, pixelSpacing);
      result.value = mmSquaredValue / 100; // Convert mm² to cm²
      result.unit = 'cm²';
      result.hasRealWorldUnits = true;
    } else {
      result.value = pixelArea;
      result.unit = 'pixels²';
      result.hasRealWorldUnits = false;
      result.warning = 'No pixel spacing data available - measurements in pixels only';
    }

    return result;
  }

  static processAngleMeasurement(annotation, imageId) {
    if (!annotation || !annotation.data || !annotation.data.handles) {
      return null;
    }

    const handles = annotation.data.handles;
    if (!handles.start || !handles.middle || !handles.end) {
      return null;
    }

    // Calculate angle
    const vector1 = {
      x: handles.start.x - handles.middle.x,
      y: handles.start.y - handles.middle.y
    };
    
    const vector2 = {
      x: handles.end.x - handles.middle.x,
      y: handles.end.y - handles.middle.y
    };

    const dot = vector1.x * vector2.x + vector1.y * vector2.y;
    const mag1 = Math.sqrt(vector1.x * vector1.x + vector1.y * vector1.y);
    const mag2 = Math.sqrt(vector2.x * vector2.x + vector2.y * vector2.y);
    
    const angleRadians = Math.acos(dot / (mag1 * mag2));
    const angleDegrees = angleRadians * (180 / Math.PI);

    return {
      value: angleDegrees,
      unit: '°',
      hasRealWorldUnits: true,
      timestamp: new Date().toISOString()
    };
  }

  static processVelocityMeasurement(annotation, imageId, velocityScale = 1) {
    if (!annotation || !annotation.data) {
      return null;
    }

    // For velocity measurements, we need additional context about the Doppler scale
    // This would typically come from DICOM metadata or user input
    let pixelVelocity = 0;

    if (annotation.data.cachedStats && annotation.data.cachedStats.max) {
      pixelVelocity = annotation.data.cachedStats.max;
    }

    return {
      value: pixelVelocity * velocityScale,
      unit: 'cm/s',
      hasRealWorldUnits: true,
      timestamp: new Date().toISOString(),
      velocityScale: velocityScale
    };
  }

  static processVTIMeasurement(annotation, imageId, velocityScale = 1, frameRate = 30) {
    if (!annotation || !annotation.data) {
      return null;
    }

    // VTI (Velocity Time Integral) calculation
    // This would typically involve tracing the velocity envelope
    let vtiValue = 0;

    if (annotation.data.cachedStats && annotation.data.cachedStats.area) {
      // Area under the velocity curve
      vtiValue = annotation.data.cachedStats.area * velocityScale / frameRate;
    }

    return {
      value: vtiValue,
      unit: 'cm',
      hasRealWorldUnits: true,
      timestamp: new Date().toISOString(),
      velocityScale: velocityScale,
      frameRate: frameRate
    };
  }

  static calculatePolygonArea(points) {
    if (!points || points.length < 3) return 0;
    
    let area = 0;
    const n = points.length;
    
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += points[i].x * points[j].y;
      area -= points[j].x * points[i].y;
    }
    
    return Math.abs(area) / 2;
  }
}

// Measurement validation and normal ranges
export class MeasurementValidator {
  static validateMeasurement(measurementType, value, unit, patientData = {}) {
    const { gender = 'all', age = null, bsa = null } = patientData;
    
    // Get normal ranges from echo measurements
    const normalRanges = this.getNormalRanges(measurementType, gender, age, bsa);
    
    if (!normalRanges) {
      return { status: 'unknown', message: 'No reference ranges available' };
    }

    if (Array.isArray(normalRanges)) {
      const [min, max] = normalRanges;
      if (value < min) {
        return { status: 'below', message: `Below normal range (${min}-${max} ${unit})` };
      } else if (value > max) {
        return { status: 'above', message: `Above normal range (${min}-${max} ${unit})` };
      } else {
        return { status: 'normal', message: `Within normal range (${min}-${max} ${unit})` };
      }
    } else if (typeof normalRanges === 'object') {
      if (normalRanges.min !== undefined && value < normalRanges.min) {
        return { status: 'below', message: `Below minimum normal (>${normalRanges.min} ${unit})` };
      } else if (normalRanges.max !== undefined && value > normalRanges.max) {
        return { status: 'above', message: `Above maximum normal (<${normalRanges.max} ${unit})` };
      } else {
        return { status: 'normal', message: 'Within normal range' };
      }
    }

    return { status: 'normal', message: 'Within normal range' };
  }

  static getNormalRanges(measurementType, gender, age, bsa) {
    // This would typically come from a comprehensive database
    // For now, return some common ranges
    const ranges = {
      'lv_edd_2d': { male: [4.2, 5.8], female: [3.9, 5.3] },
      'lv_esd_2d': { male: [2.5, 4.0], female: [2.2, 3.5] },
      'ivs_d_2d': { male: [0.6, 1.0], female: [0.6, 0.9] },
      'pw_d_2d': { male: [0.6, 1.0], female: [0.6, 0.9] },
      'la_diameter_ap': { male: [3.0, 4.0], female: [2.7, 3.8] },
      'ao_root': { male: [2.2, 3.6], female: [2.0, 3.2] }
    };

    const range = ranges[measurementType];
    if (!range) return null;

    return range[gender] || range.all || range.male;
  }
}

// Measurement storage and persistence
export class MeasurementStorage {
  static STORAGE_KEY = 'dicom_measurements';

  static saveMeasurement(imageId, measurementId, result) {
    try {
      const stored = this.getAllMeasurements();
      
      if (!stored[imageId]) {
        stored[imageId] = {};
      }
      
      stored[imageId][measurementId] = {
        ...result,
        savedAt: new Date().toISOString()
      };
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(stored));
      return true;
    } catch (error) {
      console.error('Error saving measurement:', error);
      return false;
    }
  }

  static getMeasurements(imageId) {
    try {
      const stored = this.getAllMeasurements();
      return stored[imageId] || {};
    } catch (error) {
      console.error('Error loading measurements:', error);
      return {};
    }
  }

  static getAllMeasurements() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error parsing stored measurements:', error);
      return {};
    }
  }

  static clearMeasurements(imageId = null) {
    try {
      if (imageId) {
        const stored = this.getAllMeasurements();
        delete stored[imageId];
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(stored));
      } else {
        localStorage.removeItem(this.STORAGE_KEY);
      }
      return true;
    } catch (error) {
      console.error('Error clearing measurements:', error);
      return false;
    }
  }
}

// Keyboard shortcuts manager
export class MeasurementKeyboardManager {
  constructor(callbacks) {
    this.callbacks = callbacks;
    this.boundHandler = this.handleKeyPress.bind(this);
  }

  enable() {
    document.addEventListener('keydown', this.boundHandler);
  }

  disable() {
    document.removeEventListener('keydown', this.boundHandler);
  }

  handleKeyPress(event) {
    // Don't handle shortcuts if user is typing in an input
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
      return;
    }

    switch (event.key.toLowerCase()) {
      case 'l':
        event.preventDefault();
        this.callbacks.activateLengthTool?.();
        break;
      case 'a':
        event.preventDefault();
        this.callbacks.activateAreaTool?.();
        break;
      case 'w':
        event.preventDefault();
        this.callbacks.activateWindowLevelTool?.();
        break;
      case 'escape':
        event.preventDefault();
        this.callbacks.cancelCurrentMeasurement?.();
        break;
      case 'delete':
        if (event.shiftKey) {
          event.preventDefault();
          this.callbacks.clearAllMeasurements?.();
        }
        break;
      default:
        break;
    }
  }
}

export default {
  MEASUREMENT_TOOL_TYPES,
  PixelSpacingManager,
  MeasurementProcessor,
  MeasurementValidator,
  MeasurementStorage,
  MeasurementKeyboardManager
};
